import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

import { useThemeContext } from '../../contexts/ThemeContext';
import { PrivatePayrollAuctionLoading } from '../customTheme/PrivatePayrollAuction/Loading';
import { PoweredByQITech } from '../customTheme/PrivatePayrollAuction/components/PoweredByQITech';
import { ThemedButton } from '../../components/ThemedButton';
import { FaceRecognition } from '../FaceRecognition';
import { PersonalDocument } from '../PersonalDocument';
import { OTPPage } from '../OTPPage';
import { DeviceScan } from '../DeviceScan';
import { PrivatePayrollAuctionTheme, SocialSecurityTheme } from '../customTheme';
import QISignTheme from '../../interfaces/Theme';
import * as S from './styles';

const GATE_STEPS = ['waiting_folder_conclusion', 'signed', 'completed', 'completing'];

const isGateStep = (step: string | null | undefined): boolean =>
    !step || GATE_STEPS.includes(step) || step.startsWith('folder_');

interface EnvelopeFolderChildJourneyProps {
    childSignerToken: string;
    initialStep: string;
    sessionId?: string;
    sessionTokens?: { ocr_token?: string; face_token?: string; device_token?: string };
    resolvedTheme: QISignTheme;
    onJourneyGated: () => void;
    onBack: () => void;
    setSessionExpired: () => void;
    setSignMetadata?: (metadata: any) => void;
}

export const EnvelopeFolderChildJourney = (props: EnvelopeFolderChildJourneyProps) => {
    const {
        childSignerToken,
        initialStep,
        sessionId,
        sessionTokens,
        resolvedTheme,
        onJourneyGated,
        onBack,
        setSessionExpired,
        setSignMetadata,
    } = props;

    const { clientThemeSettings } = useThemeContext();
    const theme: QISignTheme = clientThemeSettings ?? resolvedTheme;

    const [signData, setSignData] = useState<any>(() => ({
        signer_token: childSignerToken,
        session_id: uuidv4(),
        ...sessionTokens,
    }));
    const [signerStep, setSignerStepState] = useState<string>(initialStep);
    const [hydrating, setHydrating] = useState(true);
    const [hydrationError, setHydrationError] = useState(false);
    const [faceSdkReady, setFaceSdkReady] = useState<boolean>(
        () => typeof (window as any).ZaigWebFaceRecon !== 'undefined',
    );
    const gatedRef = useRef(false);

    useEffect(() => {
        let active = true;

        if (isGateStep(initialStep)) {
            if (!gatedRef.current) {
                gatedRef.current = true;
                onJourneyGated();
            }
            return () => {
                active = false;
            };
        }

        axios
            .get('/signer', { headers: { Authorization: childSignerToken } })
            .then((response) => {
                if (!active) return;
                setSignData((prevData: any) => ({
                    ...prevData,
                    ...response.data,
                    signer_token: childSignerToken,
                }));

                if (setSignMetadata && response.data?.signature_settings) {
                    setSignMetadata(response.data.signature_settings);
                }

                const status: string | undefined = response.data?.status;
                if (isGateStep(status)) {
                    if (!gatedRef.current) {
                        gatedRef.current = true;
                        onJourneyGated();
                    }
                }
            })
            .catch((error: any) => {
                if (!active) return;
                const status = error.response?.status;
                if (status === 401) {
                    setSessionExpired();
                    return;
                }
                if (status === 403 || status === 404) {
                    if (!gatedRef.current) {
                        gatedRef.current = true;
                        onJourneyGated();
                    }
                    return;
                }
                setHydrationError(true);
            })
            .finally(() => {
                if (active) setHydrating(false);
            });

        return () => {
            active = false;
        };
    }, [childSignerToken, initialStep, onJourneyGated, setSessionExpired, setSignMetadata]);

    useEffect(() => {
        if (faceSdkReady || signerStep !== 'face_recognition') return;
        const id = window.setInterval(() => {
            if (typeof (window as any).ZaigWebFaceRecon !== 'undefined') {
                setFaceSdkReady(true);
                window.clearInterval(id);
            }
        }, 200);
        return () => window.clearInterval(id);
    }, [faceSdkReady, signerStep]);

    const setSignerStep = useCallback(
        (nextStep: string | null | undefined) => {
            if (isGateStep(nextStep)) {
                if (!gatedRef.current) {
                    gatedRef.current = true;
                    onJourneyGated();
                }
                return;
            }
            if (nextStep === 'login') {
                setSessionExpired();
                return;
            }
            setSignerStepState(nextStep as string);
        },
        [onJourneyGated, setSessionExpired],
    );

    const sendAuthenticationCode = useCallback(() => {
        axios
            .post(
                '/signer/authentication',
                { event_date: new Date().toISOString() },
                { headers: { Authorization: childSignerToken } },
            )
            .catch((error: any) => {
                if (error.response?.status === 401) setSessionExpired();
            });
    }, [childSignerToken, setSessionExpired]);

    useEffect(() => {
        if (signerStep !== 'authentication_code') return;
        const tokenKey = `otp_sent_at_${childSignerToken}`;
        const lastSentTimestamp = sessionStorage.getItem(tokenKey);
        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000;
        if (!lastSentTimestamp || now - Number(lastSentTimestamp) > fiveMinutes) {
            sendAuthenticationCode();
            sessionStorage.setItem(tokenKey, String(now));
        }
    }, [signerStep, childSignerToken, sendAuthenticationCode]);

    if (hydrating) {
        return <PrivatePayrollAuctionLoading />;
    }

    if (hydrationError) {
        return (
            <S.PageWrapper>
                <S.LogoSection>
                    {theme.logo_url && <S.LogoImage src={theme.logo_url} alt="logo" />}
                </S.LogoSection>
                <S.Content>
                    <S.ErrorCard>
                        <S.ErrorText>
                            Não foi possível abrir esta proposta. Por favor, tente novamente.
                        </S.ErrorText>
                        <ThemedButton clientThemeSettings={theme} onClick={onBack}>
                            Voltar para propostas
                        </ThemedButton>
                    </S.ErrorCard>
                </S.Content>
                <S.Footer>
                    <PoweredByQITech />
                </S.Footer>
            </S.PageWrapper>
        );
    }

    const isPrivatePayrollAuction = true;

    const renderStep = () => {
        if (signerStep === 'face_recognition') {
            if (signData.provider === 'confia') {
                return (
                    <SocialSecurityTheme.ConfiaPage
                        setSignerStep={setSignerStep}
                        signData={signData}
                        setSignData={setSignData}
                    />
                );
            }
            if (!faceSdkReady) {
                return <PrivatePayrollAuctionLoading />;
            }
            const FaceRecognitionComponent = isPrivatePayrollAuction
                ? PrivatePayrollAuctionTheme.FaceRecognition
                : FaceRecognition;
            return (
                <FaceRecognitionComponent
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                    setAlreadySigned={() => undefined}
                    setCanceled={() => undefined}
                    setExpired={() => undefined}
                    appTheme="release"
                    signerHash={sessionId}
                />
            );
        }

        if (signerStep === 'personal_document') {
            const PersonalDocumentComponent = isPrivatePayrollAuction
                ? PrivatePayrollAuctionTheme.PersonalDocument
                : PersonalDocument;
            return (
                <PersonalDocumentComponent
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                    signerHash={undefined}
                />
            );
        }

        if (signerStep === 'device_scan') {
            const DeviceScanComponent = isPrivatePayrollAuction
                ? PrivatePayrollAuctionTheme.DeviceScan
                : DeviceScan;
            return (
                <DeviceScanComponent
                    setSignerStep={setSignerStep}
                    signData={signData}
                    setSignData={setSignData}
                    isBatch={false}
                    appTheme="release"
                />
            );
        }

        if (signerStep === 'authentication_code') {
            const OTPComponent = isPrivatePayrollAuction
                ? PrivatePayrollAuctionTheme.OTP
                : OTPPage;
            return (
                <OTPComponent
                    setSignerStep={setSignerStep}
                    setSignData={setSignData}
                    signData={signData}
                    onSendOTPCode={() => sendAuthenticationCode()}
                />
            );
        }

        return <PrivatePayrollAuctionLoading />;
    };

    return <>{renderStep()}</>;
};
